import { useState, useEffect, useMemo, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '../../components/Modal';
import { useCompanies, useDeleteCompany, useCompanyContacts, useBusinessEntities } from '../hooks';
import { CompanyFormModal } from './CompanyFormModal';
import type { Company, Contact, CompaniesQueryParams, ContactType, EntityStatus, BusinessEntity } from '../types';
import { ENTITY_STATUS_LABELS } from '../types';
import styles from './CompaniesPage.module.css';

const thStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: '0.75rem 1rem',
  fontSize: '0.875rem',
  color: 'var(--color-text-secondary)',
};

// --------------------------------------------------------
// Kontakt-Anzeigename
// --------------------------------------------------------
function contactDisplayName(c: Contact): string {
  if (c.type === 'ORGANIZATION') return c.companyName ?? '—';
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

const TYPE_LABELS: Record<ContactType, string> = {
  PERSON: 'Person',
  ORGANIZATION: 'Organisation',
};

// --------------------------------------------------------
// ContactSubRows — lazy-loaded Kontakte eines Unternehmens
// --------------------------------------------------------
function ContactSubRows({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useCompanyContacts(companyId, true);

  if (isLoading) {
    return (
      <tr className={styles.subRow}>
        <td colSpan={8} className={styles.subRowLoading}>
          Kontakte laden...
        </td>
      </tr>
    );
  }

  if (error) {
    return (
      <tr className={styles.subRow}>
        <td colSpan={8} className={styles.subRowError}>
          Fehler beim Laden der Kontakte
        </td>
      </tr>
    );
  }

  const contacts = data?.data ?? [];

  if (contacts.length === 0) {
    return (
      <tr className={styles.subRow}>
        <td colSpan={8} className={styles.subRowEmpty}>
          Keine Kontakte vorhanden
        </td>
      </tr>
    );
  }

  return (
    <>
      {contacts.map((contact) => (
        <tr
          key={contact.id}
          className={styles.subRow}
          onClick={() => navigate(`/crm/contacts/${contact.id}`)}
        >
          {/* Indent / Connector */}
          <td className={styles.subRowIndent}>
            <span className={styles.subRowConnector} />
          </td>
          {/* Name */}
          <td className={styles.subRowCell} style={{ fontWeight: 500 }}>
            {contactDisplayName(contact)}
          </td>
          {/* Position (anstelle Branche) */}
          <td className={styles.subRowCell}>
            {contact.position ?? '—'}
          </td>
          {/* E-Mail */}
          <td className={styles.subRowCell}>
            {contact.email ?? '—'}
          </td>
          {/* Telefon (anstelle Kontakte-Zahl) */}
          <td className={styles.subRowCell}>
            {contact.phone ?? contact.mobile ?? '—'}
          </td>
          {/* Typ-Badge (anstelle Vorgaenge) */}
          <td className={styles.subRowCell} style={{ textAlign: 'center' }}>
            <span className={styles.typeBadge} data-type={contact.type}>
              {TYPE_LABELS[contact.type] ?? '—'}
            </span>
          </td>
          {/* Status */}
          <td className={styles.subRowCell}>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: ((contact as Contact & { status?: EntityStatus }).status ?? (contact.isActive ? 'ACTIVE' : 'INACTIVE')) === 'ACTIVE'
                  ? 'var(--color-success)'
                  : ((contact as Contact & { status?: EntityStatus }).status ?? '') === 'BLOCKED'
                    ? '#991b1b'
                    : 'var(--color-error)',
              }}
              title={ENTITY_STATUS_LABELS[((contact as Contact & { status?: EntityStatus }).status ?? (contact.isActive ? 'ACTIVE' : 'INACTIVE')) as EntityStatus] ?? 'Unbekannt'}
            />
          </td>
          {/* Profil öffnen Bubble */}
          <td className={styles.subRowCell} style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/crm/contacts/${contact.id}`); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.25rem 0.625rem',
                background: '#eff6ff', color: '#2563eb',
                border: 'none', borderRadius: '9999px',
                fontSize: '0.6875rem', fontWeight: 600,
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Profil öffnen
              <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <path d="M2 5h6M5 2l3 3-3 3" />
              </svg>
            </button>
          </td>
        </tr>
      ))}
    </>
  );
}

// --------------------------------------------------------
// CompaniesPage — Hauptkomponente
// --------------------------------------------------------
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export function CompaniesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [letterFilter, setLetterFilter] = useState('');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<Company | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [groupByIndustry, setGroupByIndustry] = useState(false);
  const [businessEntityFilter, setBusinessEntityFilter] = useState('');
  const [hoveredCompanyId, setHoveredCompanyId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'name' | 'industry' | 'createdAt'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  function toggleSort(field: 'name' | 'industry' | 'createdAt') {
    if (sortField === field) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setPage(1);
  }

  function SortIcon({ field }: { field: 'name' | 'industry' | 'createdAt' }) {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: 4 }}>↕</span>;
    return <span style={{ marginLeft: 4 }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  }

  // Business entities for filter
  const { data: beData } = useBusinessEntities();
  const businessEntities = (beData?.data ?? []) as BusinessEntity[];
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset expanded rows on page change
  useEffect(() => {
    setExpandedIds(new Set());
  }, [page]);

  const params: CompaniesQueryParams = {
    page,
    pageSize: 25,
    ...(search ? { search } : {}),
    ...(letterFilter ? { letter: letterFilter } : {}),
    ...(businessEntityFilter ? { businessEntityId: businessEntityFilter } : {}),
    sort: sortField,
    order: sortOrder,
  };

  const { data, isLoading, error } = useCompanies(params);
  const deleteMutation = useDeleteCompany();

  const companies = data?.data ?? [];
  const pagination = data?.pagination;

  /** Branche-Name aus industryRef oder Fallback auf industry-Textfeld */
  const getIndustryName = (c: Company): string =>
    (c as any).industryRef?.name || c.industry || '';

  /** Gruppierte Unternehmen nach Branche (useMemo MUSS vor early returns stehen!) */
  const groupedCompanies = useMemo(() => {
    if (!groupByIndustry) return null;
    const groups = new Map<string, Company[]>();
    for (const c of companies) {
      const key = getIndustryName(c) || 'Ohne Branche';
      const list = groups.get(key) ?? [];
      list.push(c);
      groups.set(key, list);
    }
    // Sortiere Gruppen alphabetisch, "Ohne Branche" ans Ende
    return Array.from(groups.entries()).sort((a, b) => {
      if (a[0] === 'Ohne Branche') return 1;
      if (b[0] === 'Ohne Branche') return -1;
      return a[0].localeCompare(b[0], 'de');
    });
  }, [companies, groupByIndustry]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupName)) {
        next.delete(groupName);
      } else {
        next.add(groupName);
      }
      return next;
    });
  };

  const toggleExpand = (companyId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else {
        next.add(companyId);
      }
      return next;
    });
  };

  if (isLoading) return <p>Laden...</p>;
  if (error)
    return (
      <p style={{ color: 'var(--color-error)' }}>
        Fehler beim Laden der Unternehmen
      </p>
    );

  /** Rendert eine einzelne Company-Zeile (wiederverwendbar für beide Modi) */
  const renderCompanyRow = (company: Company, contactCount: number, isExpanded: boolean) => (
    <tr
      className={isExpanded ? styles.expandedRow : undefined}
      style={{
        borderBottom: '1px solid var(--color-border)',
        cursor: 'pointer',
        background: hoveredCompanyId === company.id ? 'rgba(37,99,235,0.03)' : undefined,
        transition: 'background 0.1s',
      }}
      onClick={() => navigate(`/crm/companies/${company.id}`)}
      onMouseEnter={() => setHoveredCompanyId(company.id)}
      onMouseLeave={() => setHoveredCompanyId(null)}
    >
      <td
        style={{ padding: '0.5rem', textAlign: 'center', width: '44px' }}
        onClick={(e) => contactCount > 0 && toggleExpand(company.id, e)}
      >
        {contactCount > 0 && (
          <button
            className={styles.expandBtn}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Kontakte einklappen' : 'Kontakte aufklappen'}
            title={isExpanded ? 'Kontakte einklappen' : `${contactCount} Kontakte anzeigen`}
          >
            <svg
              width="16" height="16" viewBox="0 0 16 16" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
            >
              <path d="M6 3l5 5-5 5" />
            </svg>
          </button>
        )}
      </td>
      <td style={{ padding: '0.75rem 1rem', fontSize: '0.875rem', fontWeight: 500 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.625rem' }}>
          {company.logoUrl ? (
            <img
              src={company.logoUrl}
              alt="Logo"
              style={{
                width: 28, height: 28, borderRadius: 4, objectFit: 'contain',
                border: '1px solid var(--color-border)', background: 'white', flexShrink: 0,
              }}
            />
          ) : (
            <span style={{
              width: 28, height: 28, borderRadius: 4, flexShrink: 0,
              background: 'var(--color-primary)', color: 'white',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.75rem', fontWeight: 700,
            }}>
              {(company.name?.[0] ?? '?').toUpperCase()}
            </span>
          )}
          {company.name}
          {company.businessEntity && (
            <span style={{
              fontSize: '0.6875rem', fontWeight: 700,
              padding: '0.0625rem 0.5rem', borderRadius: '999px',
              color: 'white', background: '#6366f1',
              whiteSpace: 'nowrap',
            }}>
              {company.businessEntity.shortName ?? company.businessEntity.name}
            </span>
          )}
        </span>
      </td>
      <td style={tdStyle}>
        {getIndustryName(company) || '—'}
      </td>
      <td style={tdStyle}>
        {company.email ?? '—'}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        {contactCount}
      </td>
      <td style={{ padding: '0.75rem 1rem' }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: (company.status ?? (company.isActive ? 'ACTIVE' : 'INACTIVE')) === 'ACTIVE'
              ? 'var(--color-success)'
              : (company.status ?? '') === 'BLOCKED'
                ? '#991b1b'
                : 'var(--color-error)',
          }}
          title={ENTITY_STATUS_LABELS[(company.status ?? (company.isActive ? 'ACTIVE' : 'INACTIVE')) as EntityStatus] ?? 'Unbekannt'}
        />
      </td>
      <td
        style={{ padding: '0.75rem 1rem' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: '0.375rem', opacity: hoveredCompanyId === company.id ? 1 : 0, transition: 'opacity 0.15s' }}>
          <button
            onClick={() => setEditingCompany(company)}
            title="Bearbeiten"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, padding: 0,
              background: 'transparent', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              color: 'var(--color-text-muted)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11.333 2a1.886 1.886 0 112.667 2.667L5.333 13.333 1.333 14.667l1.334-4L11.333 2z" />
            </svg>
          </button>
          <button
            onClick={() => setDeletingCompany(company)}
            title="Löschen"
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 30, height: 30, padding: 0,
              background: 'transparent', border: '1px solid #fecaca',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              color: 'var(--color-error)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );

  return (
    <div>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1 className={styles.pageHeaderTitle}>Unternehmen</h1>
          <span className={styles.pageHeaderCount}>{pagination?.total ?? 0} gesamt</span>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className={styles.pageHeaderBtn}
        >
          + Neues Unternehmen
        </button>
      </div>

      {/* Filterleiste */}
      <div className={styles.filterBar}>
        <input
          type="search"
          placeholder="Suche nach Name, Branche, E-Mail, Stadt..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className={styles.searchInput}
        />
        <select
          value={businessEntityFilter}
          onChange={(e) => { setBusinessEntityFilter(e.target.value); setPage(1); }}
          style={{
            height: '2.25rem',
            padding: '0 2rem 0 0.625rem',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
            fontSize: '0.8125rem',
            background: 'var(--color-bg-card)',
            color: 'var(--color-text)',
            cursor: 'pointer',
            appearance: 'none',
            WebkitAppearance: 'none',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.5rem center',
          }}
        >
          <option value="">Alle Gesellschaften</option>
          {businessEntities.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'de')).map((be) => (
            <option key={be.id} value={be.id}>{be.name}</option>
          ))}
        </select>
        <label
          style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none',
          }}
        >
          <span
            onClick={() => setGroupByIndustry((v) => !v)}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 34, height: 18, borderRadius: 9,
              background: groupByIndustry ? 'var(--color-primary)' : 'var(--color-border)',
              transition: 'background 0.2s', cursor: 'pointer', position: 'relative',
              flexShrink: 0,
            }}
          >
            <span style={{
              width: 14, height: 14, borderRadius: '50%', background: 'white',
              position: 'absolute', left: groupByIndustry ? 18 : 2,
              transition: 'left 0.2s', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
            }} />
          </span>
          Nach Branche gruppieren
        </label>
      </div>

      {/* A-Z Filter */}
      <div style={{ display: 'flex', gap: '0.125rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <button
          onClick={() => { setLetterFilter(''); setPage(1); }}
          style={{
            padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: !letterFilter ? 700 : 500,
            background: !letterFilter ? 'var(--color-primary)' : 'transparent',
            color: !letterFilter ? 'white' : 'var(--color-text-muted)',
            border: !letterFilter ? 'none' : '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)', cursor: 'pointer', minWidth: 36,
          }}
        >
          Alle
        </button>
        {ALPHABET.map((l) => (
          <button
            key={l}
            onClick={() => { setLetterFilter(letterFilter === l ? '' : l); setPage(1); }}
            style={{
              padding: '0.25rem 0.375rem', fontSize: '0.75rem', fontWeight: letterFilter === l ? 700 : 500,
              background: letterFilter === l ? 'var(--color-primary)' : 'transparent',
              color: letterFilter === l ? 'white' : 'var(--color-text-muted)',
              border: letterFilter === l ? 'none' : '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer', minWidth: 28,
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Tabelle */}
      <div
        style={{
          background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-sm)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr
              style={{
                borderBottom: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
              }}
            >
              <th style={{ ...thStyle, width: '40px', padding: '0.75rem 0.5rem' }} />
              <th
                style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('name')}
              >
                Name<SortIcon field="name" />
              </th>
              <th
                style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('industry')}
              >
                Branche<SortIcon field="industry" />
              </th>
              <th style={thStyle}>E-Mail</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Kontakte</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {companies.length === 0 && (
              <tr>
                <td
                  colSpan={8}
                  style={{
                    padding: '2rem',
                    textAlign: 'center',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Keine Unternehmen gefunden
                </td>
              </tr>
            )}
            {groupedCompanies
              ? groupedCompanies.map(([groupName, groupCompanies]) => {
                  const isGroupOpen = expandedGroups.has(groupName);
                  return (
                    <Fragment key={groupName}>
                      {/* Gruppen-Header (klickbar) */}
                      <tr
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => toggleGroup(groupName)}
                      >
                        <td
                          colSpan={8}
                          style={{
                            padding: '0.625rem 1rem',
                            background: 'var(--color-bg)',
                            borderBottom: '1px solid var(--color-border)',
                            fontSize: '0.8125rem',
                            fontWeight: 600,
                            color: 'var(--color-text)',
                          }}
                        >
                          <span
                            style={{
                              display: 'inline-block',
                              width: 16,
                              textAlign: 'center',
                              marginRight: '0.375rem',
                              fontSize: '0.625rem',
                              transition: 'transform 0.15s ease',
                              transform: isGroupOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                            }}
                          >
                            &#9654;
                          </span>
                          {groupName}
                          <span style={{
                            marginLeft: '0.5rem',
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            color: 'var(--color-text-muted)',
                          }}>
                            ({groupCompanies.length})
                          </span>
                        </td>
                      </tr>
                      {isGroupOpen && groupCompanies.map((company) => {
                        const contactCount = company._count?.contacts ?? 0;
                        const isExpanded = expandedIds.has(company.id);
                        return (
                          <Fragment key={company.id}>
                            {renderCompanyRow(company, contactCount, isExpanded)}
                            {isExpanded && <ContactSubRows companyId={company.id} />}
                          </Fragment>
                        );
                      })}
                    </Fragment>
                  );
                })
              : companies.map((company) => {
              const contactCount = company._count?.contacts ?? 0;
              const isExpanded = expandedIds.has(company.id);
              return (
                <Fragment key={company.id}>
                  {renderCompanyRow(company, contactCount, isExpanded)}
                  {isExpanded && <ContactSubRows companyId={company.id} />}
                </Fragment>
              );
            })}
          </tbody>
        </table>

        {/* Paginierung */}
        {pagination && pagination.totalPages > 1 && (
          <div className={styles.pagination}>
            <span>
              Seite {pagination.page} von {pagination.totalPages} (
              {pagination.total} Einträge)
            </span>
            <div className={styles.paginationButtons}>
              <button
                className={styles.paginationBtn}
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Zurück
              </button>
              <button
                className={styles.paginationBtn}
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Weiter
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal: Neues Unternehmen */}
      <CompanyFormModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
      />

      {/* Modal: Unternehmen bearbeiten */}
      <CompanyFormModal
        isOpen={!!editingCompany}
        onClose={() => setEditingCompany(null)}
        company={editingCompany}
        onSuccess={() => setEditingCompany(null)}
      />

      {/* Modal: Unternehmen löschen */}
      <Modal
        isOpen={!!deletingCompany}
        onClose={() => setDeletingCompany(null)}
        title="Unternehmen löschen"
        maxWidth="420px"
      >
        <p
          style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
            marginBottom: '0.5rem',
          }}
        >
          Soll das Unternehmen <strong>{deletingCompany?.name}</strong> wirklich
          gelöscht werden?
        </p>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-error)',
            marginBottom: '1.5rem',
          }}
        >
          Verknüpfte Kontakte und Vorgänge verlieren die Zuordnung.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={() => setDeletingCompany(null)}
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={() =>
              deletingCompany &&
              deleteMutation.mutate(deletingCompany.id, {
                onSuccess: () => setDeletingCompany(null),
              })
            }
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-error)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: deleteMutation.isPending ? 'wait' : 'pointer',
              opacity: deleteMutation.isPending ? 0.7 : 1,
            }}
          >
            {deleteMutation.isPending ? 'Löschen...' : 'Endgültig löschen'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
